import { useMutation } from "@apollo/client";
import { Button, makeStyles, Step, StepLabel, Stepper, Theme } from "@material-ui/core";
import clsx from "clsx";
import { QuitButton } from "components/Back";
import CancellationDialog from "components/CancelDialog/DialogCancellation";
import useText from "constants/text";
import { useAlertDispatch } from "contexts/AlertContext";
import {
  checkReccurenceIntervalDate,
  formatVehiclesSelected,
  isVehicleTruckChecked,
  NewRequestQuoteContext,
} from "contexts/RequestQuoteContext";
import Details from "domains/Request/component/SamaPapser/Details";
import Rescheduling from "domains/Request/component/SamaPapser/Rescheduling";
import VehicleType from "domains/Request/component/SamaPapser/VehicleType";
import { isFieldWithValue, isSlotIntervallCorrect } from "domains/Request/component/shared/utils";
import { createRequest, UPDATE_REQUEST } from "domains/Request/graphql/mutation";
import { GetClientRequests } from "domains/Request/graphql/queries";
import { ColorlibConnector } from "domains/Task/components/Job/JobLeftSide";
import { AddRequestInput, ERequestType, UpdateRequestInput } from "generated/graphql";
import { useCurrentClient } from "hooks";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Drawer } from "ui/Drawer";
import { DoubleArrowIcon } from "ui/Icons";
import { Loader } from "ui/Loader";
import { isSuccess } from "utils/https";
import {
  ClickableInformationsTerms,
  ContainerBottomForm,
  ContainerButtonsStep,
  ContainerMadFormStep,
  HeaderMadContainer,
  InformationsTerms,
  MadFormStep,
  MadTitleContainer,
  MainMadContainer,
  RootMadContainer,
  StepperContainer,
} from "../shared/MainStyles";
import RightSideDetails from "../shared/RightSideDetails";
import Recap from "./Recap";
import ValidateDialog from "./ValidateDialog";

const useStyles = makeStyles((theme: Theme) => ({
  stepper: {
    backgroundColor: "transparent",
    [theme.breakpoints.down("xs")]: {
      width: "110vw",
      position: "relative",
      padding: "16px",
      right: "42px",
    },
  },
  iconStep: {
    [theme.breakpoints.down("xs")]: {
      "& .MuiStepIcon-root": {
        color: "#d7d9e2",
      },
      "& .MuiStepIcon-completed": {
        color: "#399ebf",
      },
      "& .MuiStepIcon-active": {
        color: "#399ebf",
      },
    },
  },
  labelStep: {
    [theme.breakpoints.down("xs")]: {
      "& .MuiStepLabel-label.MuiStepLabel-alternativeLabel": {
        marginTop: "16px",
        fontSize: "12px",
      },
    },
  },
  previous: {
    width: "125px",
    marginLeft: "auto",
    display: "flex",
    [theme.breakpoints.down("xs")]: {
      marginLeft: -10,
      width: 168,
      backgroundColor: "#cfecf8",
      color: "#399ebf",
    },
  },
  next: {
    width: "125px",
    marginLeft: "24px",
    marginRight: "24px",
    display: "flex",
    [theme.breakpoints.down("xs")]: {
      marginLeft: 90,
      width: 168,
    },
  },
  iconStyle: {
    marginLeft: 20,
    transform: "rotate(180deg)",
  },
}));

export function SamaPapserForm() {
  const { text } = useText();
  const steps = [`${text.all.vehicle_type}`, `${text.all.scheduling}`, `${text.all.details}`, `${text.all.recap}`];
  const [activeStep, setActiveStep] = React.useState(0);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [notifBadge] = React.useState(false);
  const [confirm, setConfirm] = React.useState(false);
  const navigation = useNavigate();
  const { requestQuote, isEditedNT, setFieldError, setRequestQuote, truckType, setNextScreen } =
    React.useContext(NewRequestQuoteContext);
  const { client } = useCurrentClient();
  const [addRequestForClient] = useMutation(createRequest);
  const [updateRequest] = useMutation(UPDATE_REQUEST);
  const dispatchAlert = useAlertDispatch();
  const styles = useStyles();

  useEffect(() => {
    setRequestQuote({ ...requestQuote, type: ERequestType.Mad });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addNewRequest = () => {
    setIsLoading(true);
    try {
      const request: AddRequestInput = {
        type: requestQuote.type,
        vehicles: isVehicleTruckChecked(requestQuote?.vehicles)
          ? formatVehiclesSelected(requestQuote?.vehicles, truckType)
          : requestQuote?.vehicles,
        clientId: client?._id,
        address: {
          place_id: requestQuote.address.place_id,
        },
        slotStart: requestQuote.slotStart,
        slotEnd: requestQuote.slotEnd,
        reccurenceType: requestQuote.reccurenceType,
        reccurenceOptions: requestQuote.reccurenceOptions,
        productType: requestQuote.productType,
        additionnalInfos: requestQuote.additionnalInfos,
        startDate: requestQuote.startDate,
        endDate: requestQuote.endDate || requestQuote.startDate,
        dispatcher: requestQuote?.dispatcher || null,
        handler: requestQuote?.handler || null,
      };
      addRequestForClient({
        variables: {
          input: { request },
        },
        refetchQueries: [
          {
            query: GetClientRequests,
            variables: {
              queries: {
                id: client?._id,
                page: 1,
                limit: 9,
                date: "",
                status: [],
              },
            },
          },
        ],
      })
        .then((response) => {
          setIsLoading(false);
          setConfirm(false);
          const {
            data: { addRequest },
          } = response;
          if (addRequest?.code === 200) {
            dispatchAlert({
              message: text.requestQuote.successfully_created,
              severity: "success",
            });
            setRequestQuote(null);
            navigation("/tracking/request");
          } else {
            return dispatchAlert({
              message: addRequest?.message || "",
              severity: "error",
            });
          }
          return addRequest;
        })
        .catch(() => {
          setIsLoading(false);
        });
    } catch (error) {
      setIsLoading(false);
    }
  };

  const updateRequestByClint = () => {
    setIsLoading(true);
    try {
      const request: UpdateRequestInput = {
        vehicles: isVehicleTruckChecked(requestQuote?.vehicles)
          ? formatVehiclesSelected(requestQuote?.vehicles, truckType)
          : requestQuote?.vehicles,
        clientId: client?._id,
        address: {
          place_id: requestQuote.address.place_id,
        },
        slotStart: requestQuote.slotStart,
        slotEnd: requestQuote.slotEnd,
        reccurenceType: requestQuote.reccurenceType,
        reccurenceOptions: requestQuote.reccurenceOptions,
        productType: requestQuote.productType,
        additionnalInfos: requestQuote.additionnalInfos,
        startDate: requestQuote.startDate,
        endDate: requestQuote.startDate,
        dispatcher: requestQuote?.dispatcher,
        handler: requestQuote?.handler,
      };

      updateRequest({
        variables: {
          input: { id: requestQuote?._id, request },
        },
        refetchQueries: [
          {
            query: GetClientRequests,
            variables: {
              queries: {
                id: client?._id,
                page: 1,
                limit: 9,
                date: "",
                status: [],
              },
            },
          },
        ],
      })
        .then(({ data }) => {
          setIsLoading(false);
          setConfirm(false);
          if (isSuccess(data)) {
            dispatchAlert({
              message: data.results.message,
              severity: "success",
            });
            setRequestQuote(null);
            navigation("/tracking/request");
          } else {
            return dispatchAlert({
              message: data.results.message || "",
              severity: "error",
            });
          }
        })
        .catch(() => {
          setIsLoading(false);
        });
    } catch (error) {
      setIsLoading(false);
    }
  };

  const checkVehicleType = () => {
    const isInvalid =
      !requestQuote?.vehicles?.length ||
      (isVehicleTruckChecked(requestQuote?.vehicles) && !isFieldWithValue(truckType));
    setFieldError(isInvalid);
    return !isInvalid;
  };

  const isReccurenceValuesValid = () => {
    const isInvalid =
      !requestQuote?.productType?.length ||
      !isFieldWithValue(requestQuote?.reccurenceType) ||
      !isFieldWithValue(requestQuote?.address?.place_id) ||
      !checkReccurenceIntervalDate(requestQuote) ||
      !isSlotIntervallCorrect(requestQuote?.slotStart, requestQuote?.slotEnd);
    setFieldError(isInvalid);
    return !isInvalid;
  };

  const handleDesabled = () =>
    (activeStep === 0 && !requestQuote?.vehicles?.length) || (activeStep === 1 && !requestQuote?.productType?.length);

  const handleNext = () => {
    if (requestQuote) {
      switch (activeStep) {
        case 0:
          if (checkVehicleType()) setActiveStep((prevActiveStep) => prevActiveStep + 1);
          setNextScreen(true);
          setRequestQuote({ ...requestQuote, type: ERequestType.Mad });
          break;
        case 1:
          if (isReccurenceValuesValid()) {
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
            setRequestQuote({ ...requestQuote, type: ERequestType.Mad });
          }
          break;
        case 2:
          setActiveStep((prevActiveStep) => prevActiveStep + 1);
          setRequestQuote({ ...requestQuote, type: ERequestType.Mad });
          break;
        case 3:
          setConfirm(true);
          break;
        default:
          break;
      }
    } else {
      setFieldError(true);
    }
  };

  const handleBackCancel = () => {
    if (activeStep) {
      setActiveStep(activeStep - 1);
    } else {
      setOpenDialog(true);
    }
  };

  const renderStep = () => (
    <Stepper alternativeLabel activeStep={activeStep} connector={<ColorlibConnector />} className={styles.stepper}>
      {steps.map((label, index) => (
        <Step
          className={clsx(
            {
              activeStep: activeStep === index,
            },
            styles.iconStep
          )}
          key={label}
          onClick={() => {
            if (index === 0) {
              setActiveStep(index);
            }
          }}
        >
          <StepLabel className={styles.labelStep}>{label}</StepLabel>
        </Step>
      ))}
    </Stepper>
  );

  const ViewStep = React.useMemo(() => {
    switch (activeStep) {
      case 0:
        return <VehicleType />;
      case 1:
        return <Rescheduling />;
      case 2:
        return <Details />;
      case 3:
        return <Recap />;
      default:
        return <VehicleType />;
    }
  }, [activeStep]);

  const handleOpenModal = () => {
    setOpenDialog(true);
  };
  const handleCloseModal = () => {
    setOpenDialog(false);
  };

  const onConfirmCancel = () => {
    navigation("/");
  };

  return (
    <MainMadContainer>
      {activeStep !== 3 && (
        <Drawer
          value="right"
          initRightValue={false}
          notif={notifBadge}
          title={text.requestQuote.request_details}
          name={text.all.details}
          icon={
            <div className={styles.iconStyle}>
              <DoubleArrowIcon />
            </div>
          }
          content={<RightSideDetails />}
        />
      )}
      <RootMadContainer>
        <StepperContainer>
          <HeaderMadContainer>
            <QuitButton onClick={handleOpenModal} />
            <MadTitleContainer>{text.all.dedicated_courier_request}</MadTitleContainer>
            {activeStep !== 3 ? (
              <Drawer
                value="right"
                initRightValue={false}
                notif={notifBadge}
                title={text.requestQuote.request_details}
                name={text.all.details}
                icon={
                  <div className={styles.iconStyle}>
                    <DoubleArrowIcon />
                  </div>
                }
                content={<RightSideDetails />}
              />
            ) : (
              <></>
            )}
          </HeaderMadContainer>
          {renderStep()}
        </StepperContainer>
        <ContainerMadFormStep>
          <MadFormStep activeStep={activeStep}>{isLoading ? <Loader /> : ViewStep}</MadFormStep>
        </ContainerMadFormStep>
        {!isLoading && (
          <ContainerBottomForm>
            {activeStep === 3 && (
              <InformationsTerms>
                {text.requestQuote.terms_group_1}
                <ClickableInformationsTerms>
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://www.sec.gouv.sn/sites/default/files/CARTE%20ADMIN%20SEN%2019-02-2018_0.pdf"
                    style={{ width: "87%" }}
                  >
                    {text.requestQuote.terms_group_clickable}
                  </a>
                </ClickableInformationsTerms>
                {text.requestQuote.terms_group_final}
              </InformationsTerms>
            )}
            <ContainerButtonsStep>
              {activeStep > 0 && (
                <Button className={styles.previous} color="primary" variant="outlined" onClick={handleBackCancel}>
                  {activeStep > 0 ? text.all.back : text.all.cancel}
                </Button>
              )}
              <Button
                className={styles.next}
                style={{ marginLeft: activeStep > 0 ? "24px" : "auto" }}
                color="primary"
                variant="contained"
                onClick={handleNext}
                disabled={handleDesabled()}
              >
                {activeStep === 3 ? text.all.confirm : text.all.next}
              </Button>
            </ContainerButtonsStep>
          </ContainerBottomForm>
        )}
      </RootMadContainer>
      <CancellationDialog
        title={text.requestQuote.canceling_modal}
        message={text.requestQuote.canceling_confirmation_message}
        open={openDialog}
        onClose={handleCloseModal}
        onConfirm={onConfirmCancel}
      />
      <ValidateDialog
        onClose={() => setConfirm(false)}
        onConfirm={isEditedNT ? updateRequestByClint : addNewRequest}
        open={confirm}
        handleCancel={onConfirmCancel}
      />
    </MainMadContainer>
  );
}
